import { useDispatch, useSelector } from 'react-redux';
import React, { useContext, useMemo, useRef } from 'react';
import { track } from '@amplitude/analytics-browser';
import { BROADCAST_STATES, BroadcastStatus } from '../../../stores/reducers/broadcasting';
import { GreenroomState as GS } from '../../../stores/types/types';
import { showAlert } from '../../general-components/alert-service';
import { RECORDING_OPTIONS } from '../constants';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import { RESOLUTIONS } from '../../../stores/reducers/settings';
import { AmpEvent } from '../../../types/amplitude';
import {
	ScheduledStreamStatus,
	UpdateScheduledStreamStatus,
	scheduledStreamActiveStates,
} from '../../../connection/scheduled-streams';
import { SocketContext } from '../../../contexts/socket';

const useBroadcastState = () => {
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const broadcastEndpoint = useSelector((s: GS) => s.BroadcastReducer.broadcastEndpoint);
	const useCustomEndpoint = useSelector((s: GS) => s.BroadcastReducer.useCustomEndpoint);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const selectedResolution = useSelector((s: GS) => s.SettingsReducer.selectedResolution);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const startingBroadcast = useSelector((s: GS) => s.BroadcastReducer.startingBroadcast);
	const stoppingBroadcast = useSelector((s: GS) => s.BroadcastReducer.stoppingBroadcast);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);
	const selectedRecordingOpt = useSelector((s: GS) => s.BroadcastReducer.selectedRecordingOption);
	const spielbergBroadcast = useSelector((s: GS) => s.BroadcastReducer.spielbergBroadcast);
	const useDebugEncodingOptions = useSelector((s: GS) => s.DebugReducer.useDebugEncodingOptions);
	const debugEncodingOptions = useSelector((s: GS) => s.DebugReducer.debugEncodingOptions);
	const { use_spielberg: useSpielberg } = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const dispatch = useDispatch();
	const { socket } = useContext(SocketContext);

	const startTimeout = useRef<NodeJS.Timeout | null>(null);

	const broadcastStatus = useMemo(() => {
		if (useSpielberg) {
			return spielbergBroadcast?.status ?? BroadcastStatus.ready;
		}

		switch (serverState) {
			case BROADCAST_STATES.READY: return BroadcastStatus.ready;
			case BROADCAST_STATES.BROADCAST_STOPPED: return BroadcastStatus.complete;
			case BROADCAST_STATES.BROADCAST_STARTING: return BroadcastStatus.starting;
			case BROADCAST_STATES.BROADCAST_STARTED: return BroadcastStatus.active;
			case BROADCAST_STATES.BROADCAST_FAILED: return BroadcastStatus.failed;
			default: return BroadcastStatus.ready;
		}
	}, [serverState, spielbergBroadcast, useSpielberg]);

	const canGoLive = useMemo(() => host && (
		(broadcastStatus === BroadcastStatus.ready && !startingBroadcast)
		|| (broadcastStatus === BroadcastStatus.active && stoppingBroadcast)
		|| broadcastStatus === BroadcastStatus.complete
		|| broadcastStatus === BroadcastStatus.aborted
		|| broadcastStatus === BroadcastStatus.failed
		|| serverState === BROADCAST_STATES.DRAINING
		|| serverState === BROADCAST_STATES.KILLED
	), [
		host,
		broadcastStatus,
		serverState,
		startingBroadcast,
		stoppingBroadcast,
	]);

	const endActiveScheduledStream = () => {
		if (
			!token
			|| !meeting
			|| !scheduledStream
			|| !user
			|| !scheduledStreamActiveStates.includes(scheduledStream.status)
		) {
			return;
		}

		UpdateScheduledStreamStatus(
			token,
			meeting.meeting,
			scheduledStream.id,
			user.gr_user,
			ScheduledStreamStatus.Ended,
		);
	};

	// Hook to send a start message
	const startBroadcast = (
		setMockStartingBroadcast: React.Dispatch<React.SetStateAction<boolean>>,
	) => {
		if (!token || !meeting) return;
		// show starting broadcast in the UI
		setMockStartingBroadcast(true);
		// Check for broadcasting
		const isBroadcasting = selectedRecordingOpt === RECORDING_OPTIONS.STREAM_AND_RECORD;
		// Stream resolution is what is selected by the stream destination card
		// Unless recording only, we select 1080p.
		let streamResolution = (RESOLUTIONS as any)[selectedResolution];
		if (!isBroadcasting) streamResolution = RESOLUTIONS['1080p'];

		track(isBroadcasting ? AmpEvent.streamStart : AmpEvent.recordStart);

		if (user?.gr_user) {
			dispatch(BroadcastActions.setBroadcastStartedBy(user.gr_user));
			dispatch(BroadcastActions.setBroadcastStartingShot(currentShotId));
			dispatch(BroadcastActions.setCurrentRecordingStreamId(''));
		}

		if (useSpielberg) {
			dispatch(
				BroadcastActions.startSpielbergBroadcast(
					token,
					meeting.meeting,
					isBroadcasting,
					true,
					useDebugEncodingOptions ? debugEncodingOptions : undefined,
				),
			);
		} else {
			// Prevent users from spamming and causing a 500 error
			if (startTimeout.current) clearTimeout(startTimeout.current);
			startTimeout.current = setTimeout(() => {
				dispatch(
					BroadcastActions.startBroadcast(
						token,
						meeting.meeting,
						streamResolution,
						isBroadcasting,
						true,
					),
				);
			}, 5000);
		}
	};

	// Hook to send a stop message
	const stopBroadcast = (
		startupTimer: NodeJS.Timeout,
		setMockStartingBroadcast: React.Dispatch<React.SetStateAction<boolean>>,
	) => {
		if (!token || !meeting) return;
		if (startupTimer) clearInterval(startupTimer);
		setMockStartingBroadcast(false);

		dispatch(BroadcastActions.setBroadcastStartedBy(0));
		dispatch(BroadcastActions.setBroadcastStartingShot(0));
		dispatch(BroadcastActions.setCurrentRecordingStreamId(''));
		dispatch(BroadcastActions.setGoLiveButtonClicked(false));
		socket?.sendMessage('broadcast-off-show', true);

		if (startTimeout.current) clearTimeout(startTimeout.current);
		// Hack to prevent recordings from being cutoff. We don't want to wait 2s
		// if it isn't performing a recording.
		setTimeout(() => {
			if (useSpielberg) {
				dispatch(BroadcastActions.stopSpielbergBroadcast(token, meeting.meeting));
			} else {
				dispatch(BroadcastActions.stopBroadcast(token, meeting.meeting));
			}
			endActiveScheduledStream();
		}, 2000);

		const timeStarted = new Date(
			useSpielberg && spielbergBroadcast?.startTime
				? spielbergBroadcast.startTime
				: meeting.broadcast_live_start_time,
		).getTime();
		const seconds = Math.floor((Date.now() - timeStarted) / 1000);

		const event = selectedRecordingOpt === RECORDING_OPTIONS.STREAM_AND_RECORD
			? AmpEvent.streamCancel
			: AmpEvent.recordCancel;

		track(event, { durationInSeconds: seconds });
	};

	// Hook to get the current broadcast state message
	const startStopBroadcast = (
		goingLive: boolean,
		setMockStartingBroadcast: React.Dispatch<React.SetStateAction<boolean>>,
		startupTimer: NodeJS.Timeout,
	) => {
		if (!token || !meeting) return;
		switch (broadcastStatus) {
			// start (or restart) broadcast
			case BroadcastStatus.complete:
			case BroadcastStatus.aborted:
			case BroadcastStatus.ready: {
				// Verify rtmp destination exists before going live.
				// New standalone GR meetings don't have default rtmp destinations until the producer
				// enters them in streaming setup. Do NOT throw this if only in record.
				//	Note, checking for not to provide backwards compatability when recording is toggled off
				if (
					!broadcastEndpoint?.url
                    && !useCustomEndpoint
                    && selectedRecordingOpt !== RECORDING_OPTIONS.RECORD
				) {
					showAlert({
						message: 'Cannot start stream',
						description: 'No destination is selected',
						duration: 5000,
					});
					// eslint-disable-next-line consistent-return
					return setMockStartingBroadcast(false);
				}

				if (canGoLive && !goingLive) {
					startBroadcast(setMockStartingBroadcast);
					return;
				}

				if (goingLive) {
					stopBroadcast(
						startupTimer,
						setMockStartingBroadcast,
					);
					return;
				}

				return;
			}

			// cancel a starting or active the broadcast
			case BroadcastStatus.starting:
			case BroadcastStatus.active: {
				stopBroadcast(
					startupTimer,
					setMockStartingBroadcast,
				);
				return;
			}

			// attempt to restart broadcast that previously errored out
			// Do NOT throw this if only in record.
			case BroadcastStatus.failed: {
				if (
					!broadcastEndpoint?.url
                    && !useCustomEndpoint
                    && selectedRecordingOpt !== RECORDING_OPTIONS.RECORD
				) {
					showAlert({
						message: 'Cannot start broadcast',
						description: 'No destination is selected',
						duration: 5000,
					});
					// eslint-disable-next-line consistent-return
					return setMockStartingBroadcast(false);
				}

				if (!useSpielberg) {
					dispatch(
						BroadcastActions.setBroadcastState(
							BROADCAST_STATES.BROADCAST_STOPPED,
						),
					);

					stopBroadcast(
						startupTimer,
						setMockStartingBroadcast,
					);
				}

				startBroadcast(setMockStartingBroadcast);
				break;
			}

			default:
				break;
		}
	};

	return {
		startStopBroadcast,
		endActiveScheduledStream,
		canGoLive,
		broadcastStatus,
	};
};

export default useBroadcastState;
